import { useState, useEffect } from 'react';
import { toolsApi, Tool } from '../../api/tools.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';

const ToolList = () => {
  const [tools, setTools] = useState<Tool[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTool, setEditingTool] = useState<Tool | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    serialNumber: '',
    purchaseDate: '',
  });

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      setIsLoading(true);
      const data = await toolsApi.getAll();
      setTools(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Araçlar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (tool?: Tool) => {
    if (tool) {
      setEditingTool(tool);
      setFormData({
        name: tool.name,
        description: tool.description || '',
        serialNumber: tool.serialNumber || '',
        purchaseDate: tool.purchaseDate?.split('T')[0] || '',
      });
    } else {
      setEditingTool(null);
      setFormData({
        name: '',
        description: '',
        serialNumber: '',
        purchaseDate: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTool(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTool) {
        await toolsApi.update(editingTool.id, formData);
      } else {
        await toolsApi.create(formData);
      }
      await fetchTools();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu aracı silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await toolsApi.delete(id);
      await fetchTools();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  const columns: Column<Tool>[] = [
    { key: 'name', header: 'Araç Adı' },
    { key: 'description', header: 'Açıklama', render: (item) => item.description || '-' },
    { key: 'serialNumber', header: 'Seri No', render: (item) => item.serialNumber || '-' },
    {
      key: 'isActive',
      header: 'Durum',
      render: (item) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {item.isActive ? 'Aktif' : 'Pasif'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Araçlar yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Araçlar</h1>
        <Button onClick={() => handleOpenModal()}>Yeni Araç Ekle</Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <Table data={tools} columns={columns} emptyMessage="Henüz araç bulunmamaktadır" />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingTool ? 'Araç Düzenle' : 'Yeni Araç Ekle'}
      >
        <form onSubmit={handleSubmit}>
          <Input
            label="Araç Adı"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Açıklama"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <Input
            label="Seri Numarası"
            value={formData.serialNumber}
            onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
          />
          <Input
            label="Satın Alma Tarihi"
            type="date"
            value={formData.purchaseDate}
            onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
          />
          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              {editingTool ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ToolList;
